import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { dashboardAPI, contractsAPI, todosAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button, buttonVariants } from '../components/ui/button';
import { cn } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  FileText,
  Users,
  Database,
  Bookmark,
  Info,
  Pencil,
  Trash2,
  Clock,
  X,
  Eye,
  Calendar as CalendarIcon
} from 'lucide-react';
import { format, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import { tr, enUS, de } from 'date-fns/locale';

export default function Dashboard() {
  const { t, i18n } = useTranslation();

  // Map current language to date-fns locale
  const getLocale = () => {
    switch (i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };
  const currentLocale = getLocale();

  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [criticalContracts, setCriticalContracts] = useState([]);
  const [todos, setTodos] = useState([]);
  const [todoTitle, setTodoTitle] = useState('');
  const [todoDescription, setTodoDescription] = useState('');
  const [activeTodoId, setActiveTodoId] = useState(null);
  const [activeTodoCompleted, setActiveTodoCompleted] = useState(false);
  const [isAddTodoOpen, setIsAddTodoOpen] = useState(false);
  const [addingTodo, setAddingTodo] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [hideTodayReminder, setHideTodayReminder] = useState(false);
  const [isContractDetailOpen, setIsContractDetailOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, contractsRes, todosRes] = await Promise.all([
        dashboardAPI.getStats(),
        contractsAPI.getCritical(),
        todosAPI.getAll()
      ]);
      setStats(statsRes.data);
      setCriticalContracts(contractsRes.data);
      setTodos(todosRes.data);
    } catch (error) {
      toast.error(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenContractDetail = async (contractId) => {
    try {
      const response = await contractsAPI.getById(contractId);
      setSelectedContract(response.data);
      setIsContractDetailOpen(true);
    } catch (error) {
      toast.error(t('contracts.detailLoadError'));
    }
  };

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd', { locale: currentLocale });
  const today = startOfDay(new Date());
  const todayStr = format(today, 'yyyy-MM-dd', { locale: currentLocale });
  const selectedDay = startOfDay(selectedDate);
  const isSelectedToday = isSameDay(selectedDay, today);
  const isSelectedFuture = isAfter(selectedDay, today);
  const isSelectedPast = isBefore(selectedDay, today);

  const todosByDate = todos.reduce((acc, todo) => {
    const key = todo?.due_date;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(todo);
    return acc;
  }, {});

  const selectedDateTodos = todosByDate[selectedDateStr] || [];
  const primarySelectedTodo = selectedDateTodos[0] || null;
  const todayTodos = todosByDate[todayStr] || [];
  const incompleteTodayTodos = todayTodos.filter((x) => !x.completed);
  const shouldShowTodayReminder = !hideTodayReminder && todayTodos.length > 0;

  const todayTodosSorted = [...todayTodos].sort((a, b) => {
    const aCreated = a.created_at || '';
    const bCreated = b.created_at || '';
    return bCreated.localeCompare(aCreated);
  });

  const sortedTodos = [...todos].sort((a, b) => {
    const aDue = a.due_date || '';
    const bDue = b.due_date || '';
    const aIsSelected = aDue === selectedDateStr;
    const bIsSelected = bDue === selectedDateStr;
    if (aIsSelected !== bIsSelected) return aIsSelected ? -1 : 1;
    if (aDue !== bDue) {
      if (!aDue) return 1;
      if (!bDue) return -1;
      return aDue.localeCompare(bDue);
    }
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  const handleAddTodoForSelectedDate = async () => {
    const title = (todoTitle || '').trim();
    if (!title) return;

    setAddingTodo(true);
    try {
      const payload = {
        title,
        description: (todoDescription || '').trim() || null,
        due_date: selectedDateStr,
        // Modalda tamamlandı seçeneği yok; edit'te mevcut completed'i koru, create'te false başlat.
        completed: activeTodoId ? activeTodoCompleted : false,
      };

      let response;
      if (activeTodoId) {
        response = await todosAPI.update(activeTodoId, payload);
        setTodos(todos.map(t0 => (t0.id === activeTodoId ? response.data : t0)));
        toast.success(t('dashboard.todoUpdated'));
      } else {
        response = await todosAPI.create(payload);
        setTodos([response.data, ...todos]);
        toast.success(t('dashboard.todoAdded'));
      }

      setTodoTitle('');
      setTodoDescription('');
      setActiveTodoId(null);
      setActiveTodoCompleted(false);
      setIsAddTodoOpen(false);
    } catch (error) {
      toast.error(t('dashboard.todoAddError'));
    } finally {
      setAddingTodo(false);
    }
  };

  const openTodoModalForDate = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setIsAddTodoOpen(true);
    // Yeni todo ekleme akışı: bir günde birden fazla todo desteklemek için
    // modal açıldığında otomatik mevcut todo'yu seçmiyoruz.
    setActiveTodoId(null);
    setTodoTitle('');
    setTodoDescription('');
    setActiveTodoCompleted(false);
  };

  const openTodoModalForTodo = (todo) => {
    // Belirli bir todo'yu düzenleme akışı
    const due = todo?.due_date ? new Date(`${todo.due_date}T00:00:00`) : new Date();
    setSelectedDate(due);
    setIsAddTodoOpen(true);
    setActiveTodoId(todo?.id || null);
    setTodoTitle(todo?.title || '');
    setTodoDescription(todo?.description || '');
    setActiveTodoCompleted(todo?.completed === true);
  };

  const handleToggleTodo = async (todo) => {
    try {
      // Allow "completed" toggle for today AND past-due todos.
      const due = todo?.due_date ? startOfDay(new Date(todo.due_date)) : null;
      if (!due || isAfter(due, today)) return;

      await todosAPI.update(todo.id, {
        title: todo.title,
        completed: !todo.completed,
        due_date: todo.due_date || null,
        description: todo.description || null,
      });
      setTodos(todos.map(t => t.id === todo.id ? { ...t, completed: !t.completed } : t));
    } catch (error) {
      toast.error(t('dashboard.todoUpdateError'));
    }
  };

  const handleDeleteTodo = async (id) => {
    try {
      await todosAPI.delete(id);
      setTodos(todos.filter(t => t.id !== id));
      toast.success(t('dashboard.todoDeleted'));
    } catch (error) {
      toast.error(t('dashboard.todoDeleteError'));
    }
  };

  const getDaysLeftBadge = (daysRemaining) => {
    if (daysRemaining < 0) {
      return <Badge variant="destructive">{t('dashboard.expired')}</Badge>;
    }
    if (daysRemaining <= 3) {
      return <Badge variant="destructive">{t('dashboard.daysLeftCount', { count: daysRemaining })}</Badge>;
    }
    if (daysRemaining <= 7) {
      return <Badge className="bg-orange-500">{t('dashboard.daysLeftCount', { count: daysRemaining })}</Badge>;
    }
    return <Badge variant="secondary">{t('dashboard.daysLeftCount', { count: daysRemaining })}</Badge>;
  };

  const statsCards = [
    { label: t('dashboard.totalContracts'), value: stats?.total_contracts || 0, icon: FileText, bgColor: 'bg-[#333333]', iconClass: 'text-gray-300' },
    { label: t('dashboard.activeCustomers'), value: stats?.active_customers || 0, icon: Users, bgColor: 'bg-[#333333]', iconClass: 'text-gray-300' },
    { label: t('dashboard.totalCompanies'), value: stats?.total_companies || 0, icon: Database, bgColor: 'bg-[#333333]', iconClass: 'text-gray-300' },
    { label: t('dashboard.contractTypes'), value: stats?.contract_types_count || 0, icon: Bookmark, bgColor: 'bg-[#333333]', iconClass: 'text-gray-300' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat, index) => (
            <Card
              key={index}
              className="stat-card relative overflow-hidden"
              style={{ backgroundColor: 'var(--theme-accent)' }}
              data-testid={`stat-card-${index}`}
            >
              <CardContent className="p-3">
                {/* Küçük ikon + label (solda) */}
                <div className="flex items-center gap-2">
                  <div className="rounded-md bg-gray-700/60 p-1">
                    <stat.icon className={`h-5 w-5 ${stat.iconClass}`} />
                  </div>
                  <p className="label-uppercase text-white">
                    {stat.label}
                  </p>
                </div>

                {/* Value */}
                <p className="mt-1 text-xl font-bold font-mono text-white">
                  {stat.value}
                </p>

                {/* Büyük ikon (sağ arkaplan) */}
                {/* Card artık relative olduğu için ikon tam köşeye oturur */}
                <stat.icon className="pointer-events-none absolute right-0 top-0 h-24 w-24 text-white/20" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Üst blok: Todo + Takvim (görseldeki gibi yan yana) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Todos (sol) */}
          <Card data-testid="todo-card" className="h-full flex flex-col dark:bg-[#333333] dark:border-none">
            <CardHeader className="px-5 py-4 pb-3">
              <CardTitle className="text-lg font-semibold dark:text-white">{t('dashboard.todoList')}</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5 pt-0 flex-1 flex flex-col">
              <div className="flex-1">
                {selectedDateTodos.length === 0 ? (
                  <div className="text-muted-foreground dark:text-gray-400 py-4 text-center">
                    {t('dashboard.noTodos')}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[260px] overflow-y-auto scrollbar-thin pr-1">
                    {[...selectedDateTodos]
                      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                      .map((todo) => {
                        const due = todo?.due_date ? startOfDay(new Date(todo.due_date)) : null;
                        const isFuture = due ? isAfter(due, today) : false;
                        const isPast = due ? isBefore(due, today) : false;
                        const isTodayDue = due ? isSameDay(due, today) : false;
                        const canToggle = due ? !isAfter(due, today) : false; // today or past

                        const bgClass = todo.completed
                          ? 'bg-yellow-100/70 dark:bg-yellow-500/10 dark:border dark:border-yellow-500/20'
                          : isPast
                            ? 'bg-red-100/70 dark:bg-red-500/10 dark:border dark:border-red-500/20'
                            : isTodayDue
                              ? 'bg-emerald-100/70 dark:bg-emerald-500/15 dark:border dark:border-emerald-500/20'
                              : 'bg-muted/50 dark:bg-gray-700/30 dark:border dark:border-gray-600/20';

                        return (
                          <div
                            key={todo.id}
                            className={`flex items-center gap-3 p-2 rounded-lg group ${bgClass}`}
                            data-testid={`todo-item-${todo.id}`}
                          >
                            <Checkbox
                              className="opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                              checked={todo.completed}
                              onCheckedChange={() => handleToggleTodo(todo)}
                              disabled={!canToggle}
                              data-testid={`todo-checkbox-${todo.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div
                                className={`text-sm break-words whitespace-pre-wrap ${todo.completed ? 'line-through text-muted-foreground' : ''
                                  }`}
                              >
                                {todo.title}
                              </div>
                              {todo.description ? (
                                <div className="text-xs text-muted-foreground break-words whitespace-pre-wrap line-clamp-2">
                                  {todo.description}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-1">
                              {/* Future todos: allow editing */}
                              {isFuture ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                  onClick={() => openTodoModalForTodo(todo)}
                                  title={t('common.edit')}
                                  data-testid={`edit-todo-${todo.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              ) : null}

                              <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                onClick={() => handleDeleteTodo(todo.id)}
                                data-testid={`delete-todo-${todo.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Buton kartın en altında */}
              <Button
                className="w-full bg-[#333333] dark:bg-[#666666] hover:bg-[#444444] dark:hover:bg-[#777777] text-white mt-auto"
                onClick={() => openTodoModalForDate(new Date())}
                data-testid="add-todo-btn"
              >
                + {t('dashboard.addNewTodo')}
              </Button>
            </CardContent>
          </Card>

          {/* Takvim (sağ) - görseldeki büyük tarih + küçük takvim */}
          <Card data-testid="calendar-card" className="h-full dark:bg-[#333333] dark:border-none">
            <CardContent className="p-5">
              {/* Not: justify-between kalan alanı açtığı için boşluk değişmiyordu */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-8 sm:gap-16 mx-auto w-fit">
                <div className="text-center">
                  <div className="text-5xl font-bold leading-none text-[#333333] dark:text-white">
                    {format(selectedDate, 'd', { locale: currentLocale })}
                  </div>
                  <div className="mt-2 text-sm font-medium text-[#333333] dark:text-white whitespace-nowrap">
                    {format(selectedDate, 'MMMM | yyyy', { locale: currentLocale })}
                  </div>
                </div>

                {/* Takvim: todo ekleme yok; sadece gün seçerek listede göster */}
                <div className="flex justify-center sm:justify-end">
                  <Calendar
                    mode="single"
                    required
                    locale={currentLocale}
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (!date) return;
                      setSelectedDate(date);
                    }}
                    classNames={{
                      // Make selected day render as a circle (not a square)
                      cell:
                        "relative p-0 text-center text-sm w-9 h-9 focus-within:relative focus-within:z-20 [&:has([aria-selected])]:rounded-full",
                      // Avoid the default filled button background overriding our circle content
                      day_selected:
                        "bg-transparent text-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent focus:text-foreground",
                      day_today: "bg-transparent text-foreground dark:text-white",
                      caption_label: "text-[#333333] dark:text-white font-bold",
                      head_cell: "text-muted-foreground dark:text-gray-400 font-normal text-[0.8rem] w-9",
                      day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 dark:text-gray-200",
                      nav_button: cn(
                        buttonVariants({ variant: "outline" }),
                        "h-7 w-7 bg-[#333333] text-white hover:bg-[#444444] hover:text-white p-0 opacity-100 dark:border-gray-600"
                      ),
                    }}
                    components={{
                      DayContent: ({ date, activeModifiers }) => {
                        const key = format(date, 'yyyy-MM-dd');
                        const list = todosByDate[key] || [];
                        const isSelected = activeModifiers?.selected === true;
                        const isTodayMod = activeModifiers?.today === true || isSameDay(date, new Date());

                        const d = startOfDay(date);
                        const isF = isAfter(d, today);
                        const isP = isBefore(d, today);
                        const anyIncomplete = list.some((x) => !x.completed);

                        // If it's today, we check for incomplete todos to show emerald pulse
                        if (isTodayMod) {
                          const hasIncompleteToday = list.length > 0 && anyIncomplete;
                          const todayBgClass = hasIncompleteToday
                            ? 'bg-emerald-500 animate-pulse'
                            : 'bg-[#333333] dark:bg-[#666666]';

                          return (
                            <span
                              className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white ${todayBgClass}`}
                            >
                              {date.getDate()}
                            </span>
                          );
                        }

                        // If no todos and not selected, just show the number
                        if (list.length === 0 && !isSelected) return <span>{date.getDate()}</span>;

                        // Render the day number inside a colored circle for todos or selection
                        let circleClass = 'bg-slate-300/40 text-slate-900 dark:bg-zinc-600 dark:text-white';

                        if (list.length > 0) {
                          if (isF) {
                            circleClass = 'bg-slate-300/40 text-slate-900 dark:bg-zinc-600 dark:text-white';
                          } else if (isP) {
                            circleClass = anyIncomplete
                              ? 'bg-red-500/35 text-red-950 dark:bg-red-900/60 dark:text-red-50'
                              : 'bg-slate-300/40 text-slate-900 dark:bg-zinc-600 dark:text-white';
                          }
                        } else if (isSelected) {
                          // Selected day with no todo: show theme circle
                          return (
                            <span
                              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white bg-[#333333] dark:bg-[#666666]"
                            >
                              {date.getDate()}
                            </span>
                          );
                        }

                        // Special handling for future/past todos with circles
                        return (
                          <span
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${circleClass}`}
                          >
                            {date.getDate()}
                          </span>
                        );
                      },
                    }}
                    today={new Date()}
                    className="rounded-md"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alt blok: Bitime en yakın sözleşmeler (tam genişlik) */}
        <Card data-testid="critical-contracts-card" className="dark:bg-[#333333] dark:border-none">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold dark:text-white">
              {t('dashboard.criticalContracts')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header dark:bg-[#444444] dark:border-none">
                    <TableHead className="dark:text-gray-200">{t('contracts.customer')}</TableHead>
                    <TableHead className="dark:text-gray-200">{t('dashboard.daysLeft')}</TableHead>
                    <TableHead className="dark:text-gray-200">{t('contracts.type')}</TableHead>
                    <TableHead className="dark:text-gray-200">{t('contracts.company')}</TableHead>
                    <TableHead className="text-center dark:text-gray-200">{t('dashboard.detail')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {criticalContracts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12">
                        <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">{t('dashboard.noCriticalContracts')}</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    criticalContracts.slice(0, 10).map((contract) => (
                      <TableRow
                        key={contract.id}
                        className="table-row dark:hover:bg-[#444444] dark:border-gray-700/50"
                        data-testid={`critical-contract-${contract.id}`}
                      >
                        <TableCell className="table-cell font-medium">
                          {contract.customer_name || '-'}
                        </TableCell>
                        <TableCell className="table-cell">
                          {getDaysLeftBadge(contract.days_remaining)}
                        </TableCell>
                        <TableCell className="table-cell">
                          {contract.contract_type_name || '-'}
                        </TableCell>
                        <TableCell className="table-cell">
                          {contract.company_name || '-'}
                        </TableCell>
                        <TableCell className="table-cell text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full"
                            onClick={() => handleOpenContractDetail(contract.id)}
                            data-testid={`view-contract-${contract.id}`}
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Add Todo Modal (for selected calendar date) */}
        <Dialog open={isAddTodoOpen} onOpenChange={setIsAddTodoOpen}>
          <DialogContent className="sm:max-w-sm dark:bg-[#333333] dark:border-none dark:text-white" data-testid="add-todo-modal">
            <DialogHeader>
              <DialogTitle className="dark:text-white">
                {t('dashboard.addTodoForDate', { date: format(selectedDate, 'dd MMMM yyyy', { locale: currentLocale }) })}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground dark:text-gray-400">
                  {t('dashboard.calendar')}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal dark:bg-[#666666] dark:border-none dark:text-white dark:hover:bg-[#777777]",
                        !selectedDate && "text-muted-foreground"
                      )}
                      data-testid="todo-date-popover-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? (
                        format(selectedDate, "PPP", { locale: currentLocale })
                      ) : (
                        <span>{t('dashboard.selectDate')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 dark:bg-[#333333] dark:border-gray-700" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      locale={currentLocale}
                      initialFocus
                      classNames={{
                        head_cell: "text-muted-foreground dark:text-gray-400 font-normal text-[0.8rem] w-9",
                        day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 dark:text-gray-200",
                        nav_button: cn(
                          buttonVariants({ variant: "outline" }),
                          "h-7 w-7 bg-white dark:bg-[#666666] text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#777777] p-0 opacity-100 dark:border-none"
                        ),
                        caption_label: "text-sm font-medium dark:text-white",
                        day_today: "bg-[#333333] dark:bg-[#666666] text-white rounded-full",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <textarea
                value={todoTitle}
                onChange={(e) => setTodoTitle(e.target.value)}
                placeholder={t('dashboard.todoTitlePlaceholder')}
                data-testid="todo-title-input"
                disabled={isSelectedPast}
                rows={2}
                className="flex w-full rounded-md border border-input dark:border-gray-700 bg-transparent dark:bg-[#444444]/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground dark:placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none dark:text-white"
              />
              <textarea
                value={todoDescription}
                onChange={(e) => setTodoDescription(e.target.value)}
                placeholder={t('dashboard.todoDescriptionPlaceholder')}
                data-testid="todo-description-input"
                disabled={isSelectedPast}
                rows={4}
                className="flex w-full rounded-md border border-input dark:border-gray-700 bg-transparent dark:bg-[#444444]/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground dark:placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none dark:text-white"
              />

              {/* İstek: Modalda "Als erledigt markieren" bölümü kaldırıldı */}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddTodoOpen(false)} className="dark:bg-transparent dark:border-gray-700 dark:text-gray-300 dark:hover:bg-[#444444]">
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleAddTodoForSelectedDate}
                disabled={addingTodo || isSelectedPast || !todoTitle.trim()}
                className="dark:bg-[#666666] dark:hover:bg-[#777777] dark:text-white dark:border-none"
              >
                {activeTodoId ? t('common.update') : t('common.add')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contract Detail Modal (same as Contracts page) */}
        <Dialog open={isContractDetailOpen} onOpenChange={setIsContractDetailOpen}>
          <DialogContent className="sm:max-w-lg dark:bg-[#333333] dark:border-none dark:text-white" data-testid="contract-detail-modal">
            <DialogHeader>
              <DialogTitle className="text-lg font-normal border-b dark:border-gray-700 pb-2 dark:text-white">
                {t('contracts.detailTitle')}
              </DialogTitle>
            </DialogHeader>

            {selectedContract && (
              <div className="space-y-2">
                <div className="flex flex-col">
                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.customer')}:</span>
                    <span className="text-sm">{selectedContract.customer_name || '-'}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.type')}:</span>
                    <span className="text-sm">{selectedContract.contract_type_name || '-'}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.company')}:</span>
                    <span className="text-sm">{selectedContract.company_name || '-'}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.startDate')}:</span>
                    <span className="text-sm">{selectedContract.start_date}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.endDate')}:</span>
                    <span className="text-sm">{selectedContract.end_date}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.daysRemaining')}:</span>
                    <span className="text-sm">{selectedContract.days_remaining} {t('contracts.days')}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.orderNo')}:</span>
                    <span className="text-sm">{selectedContract.order_number || '-'}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.contractNo')}:</span>
                    <span className="text-sm">{selectedContract.contract_number || '-'}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                    <span className="font-semibold text-sm">{t('contracts.description')}:</span>
                    <span className="text-sm">{selectedContract.description || '-'}</span>
                  </div>
                </div>

                <div className="border dark:border-gray-700 rounded-lg p-2 bg-muted/20 dark:bg-[#444444]/40">
                  <h3 className="font-semibold mb-1 text-sm dark:text-white">{t('contracts.history')}:</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('contracts.noHistory')}</p>
                </div>

                <div className="border dark:border-gray-700 rounded-lg p-2 bg-muted/20 dark:bg-[#444444]/40">
                  <h3 className="font-semibold mb-1 text-sm dark:text-white">{t('contracts.contractFiles')}:</h3>
                  <div className="grid grid-cols-1 gap-2">
                    {selectedContract.files && selectedContract.files.length > 0 ? (
                      selectedContract.files.map((file, index) => (
                        <div
                          key={index}
                          className="border dark:border-gray-700 rounded-lg p-4 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#444444] transition-colors bg-gray-50/50 dark:bg-white/5"
                          onClick={async () => {
                            if (file.url) {
                              try {
                                const response = await contractsAPI.downloadFile(file.url);
                                // Keep behavior aligned with Contracts page: open via Blob URL (not copyable across browsers)
                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.target = '_blank';
                                link.rel = 'noopener noreferrer';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => {
                                  try {
                                    window.URL.revokeObjectURL(blobUrl);
                                  } catch {
                                    // ignore
                                  }
                                }, 60_000);
                              } catch (error) {
                                const detail =
                                  error?.response?.data?.detail ||
                                  error?.response?.statusText ||
                                  null;
                                toast.error(detail || t('contracts.exportError'));
                              }
                            } else {
                              toast.info(t('common.loading'));
                            }
                          }}
                        >
                          <div className="w-12 h-16 border-2 border-gray-300 dark:border-gray-600 rounded flex items-center justify-center bg-white dark:bg-white/10">
                            <FileText className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-sm">{file.name}</p>
                            <p className="text-xs text-gray-500">{file.size ? `${file.size} bytes` : ''}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        -
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {shouldShowTodayReminder ? (
        <div
          className="fixed right-4 bottom-4 z-50 w-[320px] max-w-[calc(100vw-2rem)]"
          data-testid="today-todo-reminder"
        >
          <div className="rounded-xl border bg-card dark:bg-[#333333] dark:border-none text-card-foreground dark:text-white shadow-lg">
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-full bg-muted dark:bg-[#444444] p-2">
                    <Clock className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold leading-5">
                      {t('dashboard.todayReminderTitle')}
                    </div>
                    <div className="text-sm text-muted-foreground dark:text-gray-400">
                      {incompleteTodayTodos.length > 0
                        ? t('dashboard.todayReminderCount', { count: incompleteTodayTodos.length })
                        : t('dashboard.todayReminderAllDone')}
                    </div>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 dark:hover:bg-[#444444] dark:text-gray-400 dark:hover:text-white"
                  onClick={() => setHideTodayReminder(true)}
                  aria-label={t('common.close')}
                  data-testid="today-todo-reminder-close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="mt-3 space-y-2">
                {(incompleteTodayTodos.length > 0 ? incompleteTodayTodos : todayTodos)
                  .slice(0, 3)
                  .map((todo) => (
                    <div key={todo.id} className="flex items-start gap-2 text-sm">
                      <span
                        className={`mt-1 inline-block h-2 w-2 rounded-full ${todo.completed ? 'bg-emerald-500' : 'bg-red-500'
                          }`}
                      />
                      <span className={`${todo.completed ? 'line-through text-muted-foreground dark:text-gray-500' : 'dark:text-gray-200'}`}>
                        {todo.title}
                      </span>
                    </div>
                  ))}

                {(incompleteTodayTodos.length > 0 ? incompleteTodayTodos : todayTodos).length > 3 ? (
                  <div className="text-xs text-muted-foreground dark:text-gray-500">
                    {t('dashboard.todayReminderMore', {
                      count: (incompleteTodayTodos.length > 0 ? incompleteTodayTodos : todayTodos).length - 3,
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </Layout>
  );
}
